import React, { useState, useEffect, useRef } from "react";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { arrayUnion, doc, updateDoc } from "firebase/firestore";
import PurchaseModal from "./PurchaseModal";
import { usePurchasedPrices } from "../context/PurchasedPricesContext"; // Import the context

const LiveRow = ({ liveMunis, slideLeft, slideRight }) => {
  const [likedMunis, setLikedMunis] = useState({});
  const [saved, setSaved] = useState(false);
  const { user } = UserAuth();
  const { purchasedPrices } = usePurchasedPrices(); // Use the context
  const [isOverflowing, setIsOverflowing] = useState(false);
  const sliderRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMuni, setSelectedMuni] = useState(null);
  const navigate = useNavigate();

  const userDocRef = doc(db, "users", `${user?.uid}`);

  const savedMuni = async (muni) => {
    if (user?.uid) {
      setLikedMunis((prevLikedMunis) => ({
        ...prevLikedMunis,
        [muni.id]: !prevLikedMunis[muni.id],
      }));
      setSaved(true);
      await updateDoc(userDocRef, {
        savedMunis: arrayUnion({
          id: muni.id,
          title: muni.title,
          img: muni.thumbnail,
        }),
      });
    } else {
      alert("Please log in to save a favourite");
    }
  };

  useEffect(() => {
    if (sliderRef.current) {
      setIsOverflowing(
        sliderRef.current.scrollWidth > sliderRef.current.clientWidth
      );
    }
  }, [liveMunis]);

  const openPurchaseModal = (muni) => {
    setSelectedMuni(muni);
    setModalOpen(true);
  };

  const closePurchaseModal = () => {
    setModalOpen(false);
    setSelectedMuni(null);
  };

  // Filter out munis with the tag "test"
  const filteredMunis = liveMunis.filter((muni) => muni.tags !== "test");

  // If there are no munis after filtering, don't render the section
  if (filteredMunis.length === 0) {
    return null;
  }

  return (
    <>
      <h2 className="text-red-500 font-bold md:text-xl p-4">
        Digital Broadcasts Live Now
      </h2>
      <div className="relative flex items-center group pl-2">
        {isOverflowing && (
          <MdChevronLeft
            onClick={slideLeft}
            className="bg-yellow-400 left-0 rounded-full absolute opacity-50 hover:opacity-100 cursor-pointer z-10 hidden group-hover:block"
            size={40}
          />
        )}
        <div
          ref={sliderRef}
          id={"liveSlider"}
          className="w-full h-full overflow-x-scroll whitespace-nowrap scroll-smooth scrollbar-hide"
        >
          {filteredMunis.map((muni, id) => {
            const isPurchased = muni.prodId
              ? purchasedPrices.includes(muni.prodId)
              : true; // Allow clickable if no prodId

            return (
              <div
                key={id}
                className={`w-[160px] sm:w-[200px] md:w-[240px] lg:w-[280px] inline-block relative p-2 ${
                  isPurchased ? "cursor-pointer" : "cursor-not-allowed"
                }`}
              >
                {isPurchased ? (
                  <>
                    <Link to={`/fc/${muni.id}`}>
                      <img
                        className="w-full h-auto block rounded"
                        src={muni.thumbnail}
                        alt={muni.title}
                      />
                      <div className="absolute top-0 left-0 w-full h-full hover:bg-black/80 opacity-0 hover:opacity-100 text-white">
                        <p className="white-space-normal text-xs md:text-sm font-bold flex justify-center items-center h-full text-center">
                          <span className="text-white justify-center items-center text-wrap text-center">
                            {muni.title}
                          </span>
                        </p>
                      </div>
                      <p className="absolute top-4 right-4 bg-red-600 text-white text-xs px-2 py-1 rounded">
                        LIVE
                      </p>

                      <p className="white-space-normal text-xs md:text-sm font-bold flex justify-center items-center h-full text-center text-wrap">
                        <span className="text-white justify-center items-center text-center">
                          {muni.title}
                        </span>
                      </p>
                    </Link>
                    <p
                      className="absolute top-4 left-4"
                      onClick={() => savedMuni(muni)}
                    >
                      {likedMunis[muni.id] ? (
                        <FaHeart className="text-red-300" />
                      ) : (
                        <FaRegHeart className="text-red-300" />
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <div
                      onClick={() => {
                        if (user) {
                          openPurchaseModal(muni);
                        } else {
                          navigate("/login");
                        }
                      }}
                    >
                      {" "}
                      <div className="bg-black/80 w-full h-auto opacity-25">
                        <img
                          className="w-full h-auto block rounded"
                          src={muni.thumbnail}
                          alt={muni.title}
                        />
                        <p className="absolute top-4 right-4 bg-red-600 text-white text-xs px-2 py-1 rounded">
                          LIVE
                        </p>
                      </div>
                      <div className="absolute top-0 left-0 w-full h-full text-white flex items-center justify-center">
                        <p className="">
                          <button className="bg-red-600 rounded text-xs md:text-sm font-bold text-center px-2 py-1">
                            Click to purchase
                          </button>
                        </p>
                      </div>
                    </div>
                    <p className="white-space-normal text-xs md:text-sm font-bold flex justify-center items-center h-full text-center text-wrap">
                      <span className="text-white justify-center items-center text-center">
                        {muni.title}
                      </span>
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {isOverflowing && (
          <MdChevronRight
            onClick={slideRight}
            className="bg-yellow-400 right-0 rounded-full absolute opacity-50 hover:opacity-100 cursor-pointer z-10 hidden group-hover:block"
            size={40}
          />
        )}
      </div>
      <PurchaseModal
        isOpen={modalOpen}
        onClose={closePurchaseModal}
        muni={selectedMuni}
      />
    </>
  );
};

export default LiveRow;
